<template>
	<div class="ferment-gauge-container" :class="{'not-enough-data': this.fermenter.currentFerment && (fermentNotEnoughData(this.fermenter.currentFerment.data) || fermenter.currentFerment.data.blendComponents.data.length > 0)}">
		<canvas id="gauge_{{fermenter.id}}" class="ferment-gauge" width="100%"></canvas>
	</div>
	<div class="progress-container-right">
		<div class="progress-label">
			{{ getLastSugar() }} {{{ units.sugarUnit() }}}<br />
			{{ getLastTemperature() }} {{{ units.temperatureUnit() }}}<br />
		</div>
		<div class="progress">
			<div
				v-if="!fermentIsBlended"
				class="progress-bar progress-bar-primary progress-bar"
				role="progressbar"
				aria-valuenow="{{ gaugeData.progress || 0 }}"
				aria-valuemin="0"
				aria-valuemax="100"
				:style="{'width': (gaugeData.progress || 0)+'%'}">
				<span>{{ gaugeData.progress ? gaugeData.progress+'%' : '' }}</span>
			</div>
		</div>
	</div>
</template>

<style>
	.progress-container-right .progress .progress-bar {
		transition: none;
	}

	.ferment-gauge-container.not-enough-data {
		opacity: 0.5;
	}
</style>

<script>
	import { simulatorSetup } from 'vx/getters'
	import { RadialGauge } from 'canvas-gauges'
	import units from 'utils/units'
	import FermenterHelpers from 'utils/fermenters'

	export default {
		props: ['fermenter'],

		vuex: {
			getters: {
				simulatorSetup
			}
		},

		data() {
			return {
				gauge: null,
				gaugeData: {
					baume_initial: null,
					baume_current: null,
					temp_current: null,
					progress: 0,
					predicted_duration: 0
				},
				ferment: {},
				units
			};
		},

		computed: {
			fermentIsBlended () {
				return this.ferment && this.ferment.blendComponents && this.ferment.blendComponents.data.length > 0
			}
		},

		ready() {
			if (this.fermenter.currentFerment) {
				this.ferment = this.fermenter.currentFerment.data;
				this.gaugeData = this.ferment.gauge.data;
			}

			if (this.gaugeData.baume_initial && this.gaugeData.baume_current && this.ferment.blendComponents.data.length === 0) {
				this.gaugeData.progress = Math.round(((this.gaugeData.baume_initial - this.gaugeData.baume_current) / this.gaugeData.baume_initial) * 100);

				if (this.gaugeData.progress > 100) {
					this.gaugeData.progress = 100;
				}
			}

			let gaugeNumbers = {
				min: -4,
				max: 4,
				ticks: [8, 7, 6, 5, 4, 3, 2, 1, 0],
				minorTicks: 5,
				highlights: [
					{ from: -4, to: -2, color: '#5791d0' },
					{ from: -2, to: 0 , color: '#70bd75' },
					{ from: 0, to: 2, color: '#70bd75' },
					{ from: 2, to: 4, color: '#bc4841' }
				],
				needle: 0
			};

			// Determine gauge ranges based on the predicted and desired durations...
			// To start with we'll get the difference between the predicted and the
			// desired duration and then round to the nearest EVEN number.
			if (this.gaugeData.predicted_duration && this.ferment && ! this.fermentNotEnoughData(this.ferment) && this.ferment.blendComponents.data.length === 0) {
				let predictedDuration = parseFloat(this.gaugeData.predicted_duration),
					desiredDuration = parseFloat(this.ferment.duration),
					difference = 2 * Math.round(Math.abs(Math.ceil(predictedDuration) - Math.ceil(desiredDuration)) / 2),
					stuckFerment = false;

				if (difference <= 0) {
					difference = 4;
				} else if (difference < 4) {
					difference *= 2;
				}

				// Half of this number is the amount we need to add either side of
				// the desired ferment duration so that we have a nice even amount
				// of numbers either side.
				gaugeNumbers.min = desiredDuration - difference;
				gaugeNumbers.max = desiredDuration + difference;
				gaugeNumbers.ticks = [];

				if (gaugeNumbers.max - gaugeNumbers.min > 20) {
					gaugeNumbers.min = desiredDuration - 10;
					gaugeNumbers.max = desiredDuration + 10;

					stuckFerment = true;

					// If difference between predicted duration and max gauge number
					// (i.e., If the predicted duration is closer to the max)..
					if ((Math.abs(predictedDuration - gaugeNumbers.max)) < (Math.abs(predictedDuration - gaugeNumbers.min))) {
						// Set the needle to the min number (i.e., all the way to the left/highest number)
						gaugeNumbers.needle = gaugeNumbers.min;
					}else {
						// Else set the needle to the max number (i.e., all the way to the right/lowest number)
						gaugeNumbers.needle = gaugeNumbers.max;
					}
				} else {
					gaugeNumbers.needle = (gaugeNumbers.max - predictedDuration) + gaugeNumbers.min;
				}

				// If the difference between the min and max divided by the minor
				// ticks is less than 4 then we want to set the minor ticks to 2
				// so that we have more detailed numbers on the gauge.
				if ((gaugeNumbers.max - gaugeNumbers.min) / gaugeNumbers.minorTicks < 2) {
					gaugeNumbers.minorTicks = 2;
				} else if ((gaugeNumbers.max - gaugeNumbers.min) / gaugeNumbers.minorTicks < 4) {
					gaugeNumbers.minorTicks = 4;
				} else if ((gaugeNumbers.max - gaugeNumbers.min) / gaugeNumbers.minorTicks > 10) {
					gaugeNumbers.minorTicks = 8;
				}

				for (let i = gaugeNumbers.min; i <= gaugeNumbers.max; i += gaugeNumbers.minorTicks) {
					if (stuckFerment && i == gaugeNumbers.max) {
						i = i.toString()+'+';
					}

					gaugeNumbers.ticks.push(i);
				}

				gaugeNumbers.ticks.reverse();

				gaugeNumbers.highlights = [
					{ from: gaugeNumbers.min, to: desiredDuration - 2, color: '#5791d0' },
					{ from: desiredDuration - 2, to: desiredDuration + 2, color: '#70bd75' },
					{ from: desiredDuration + 2, to: gaugeNumbers.max, color: '#bc4841' }
				];
			}

			this.gauge = new RadialGauge({
				renderTo: `gauge_${this.fermenter.id}`,
				width: $('.ferment-gauge-container').width(),
				height: $('.ferment-gauge-container').outerHeight() * 1.5,
				title: 'Days',
				minValue: gaugeNumbers.min,
				maxValue: gaugeNumbers.max,
				ticksAngle: 180,
				startAngle: 90,
				majorTicks: gaugeNumbers.ticks,
				minorTicks: gaugeNumbers.minorTicks,
				strokeTicks:true,
				needleType: 'arrow',
				colorNeedle: 'rgba(37, 45, 51, 0.54)',
				colorNeedleEnd: 'rgba(37, 45, 51, 0.54)',
				needleWidth: 1.5,
				valueBox: false,
				borderOuterWidth: 0,
				borderMiddleWidth: 0,
				borderInnerWidth: 0,
				borderShadowWidth: 0,
				highlights: gaugeNumbers.highlights,
				glow: false,
				animationDelay: 0,
				animationDuration: 0,
				value: gaugeNumbers.needle
			});

			this.gauge.draw();

			$(window).on('resize', () => {
				try {
					this.gauge.update({
						width: $('.ferment-gauge-container').width(),
						height: $('.ferment-gauge-container').outerHeight() * 1.5
					});
				} catch (exception) {
					// On Safari this errors because it seems the handler is bound before
					// the DOM is ready (somehow) so it throws this weird invalid state
					// exception. We'll just catch it and fail silently as this does
					// not seem to affect it at all. When it errors it does break
					// it though, which isn't good. Hooray for other browsers
					// all behaving differently! Sigh...
				}
			});
		},

		methods: {
			...FermenterHelpers,

			getLastSugar() {
				let lastSugar = '-';

				if (this.fermenter.currentFerment && (this.fermentNotEnoughData(this.fermenter.currentFerment.data) || this.fermentOutOfSync() || this.fermenter.currentFerment.data.blendComponents.data.length > 0)) {
					this.fermenter.currentFerment.data.dailyData.data.forEach(day => {
						if (day.baume) {
							lastSugar = day.baume;
						}
					});
				} else {
					lastSugar = this.gaugeData.baume_current == null ? '-' : this.gaugeData.baume_current;
				}

				return (lastSugar == '-' ? lastSugar : units.baumeToBrix(lastSugar).toFixed(2));
			},

			getLastTemperature() {
				let lastTemperature = '-',
					lastDayWithBaume,
					lastDayWithTemperature;

				if (this.fermenter.currentFerment && (this.fermentNotEnoughData(this.fermenter.currentFerment.data) || this.fermentOutOfSync() || this.fermenter.currentFerment.data.blendComponents.data.length > 0)) {
					this.fermenter.currentFerment.data.dailyData.data.forEach(day => {
						if (day.temp) {
							lastDayWithTemperature = day;
						}

						if (day.baume) {
							lastDayWithBaume = day;
						}
					});
				} else {
					lastTemperature = this.gaugeData.temp_current == null ? '-' : this.gaugeData.temp_current;
				}

				if (lastDayWithTemperature) {
					lastTemperature = lastDayWithTemperature.temp;
				}

				if (lastDayWithBaume && lastDayWithBaume.temp) {
					lastTemperature = lastDayWithBaume.temp;
				}

				return (lastTemperature == '-' ? lastTemperature : units.celsiusToFahrenheit(lastTemperature).toFixed(1));
			},

			fermentOutOfSync() {
				return this.fermenter.currentFerment && ! this.fermentNotEnoughData(this.fermenter.currentFerment.data) && ! this.fermenter.currentFerment.data.simulated;
			}
		}
	}
</script>



// WEBPACK FOOTER //
// gauge.vue?c54f24f0